import * as React from 'react'
import { TrendingDown, TrendingUp, Loader2 } from 'lucide-react'
import { Card, CardContent } from './card'
import { cn } from '../../lib/utils'

// ============================================================================
// MetricCard — standard KPI card with optional border accent
// ============================================================================

// Tailwind's JIT can only see complete class strings in source. Map tokens
// to full classes so the scanner picks them up.
const BORDER_COLOR_CLASS: Record<string, string> = {
  'amber-300':   'tw:border-l-amber-300',
  'amber-500':   'tw:border-l-amber-500',
  'blue-400':    'tw:border-l-blue-400',
  'blue-500':    'tw:border-l-blue-500',
  'emerald-500': 'tw:border-l-emerald-500',
  'lime-500':    'tw:border-l-lime-500',
  'orange-400':  'tw:border-l-orange-400',
  'orange-500':  'tw:border-l-orange-500',
  'purple-500':  'tw:border-l-purple-500',
  'red-400':     'tw:border-l-red-400',
  'rose-500':    'tw:border-l-rose-500',
  'sky-500':     'tw:border-l-sky-500',
  'slate-500':   'tw:border-l-slate-500',
  'teal-700':    'tw:border-l-teal-700',
  'violet-400':  'tw:border-l-violet-400',
  'violet-500':  'tw:border-l-violet-500',
  'yellow-500':  'tw:border-l-yellow-500',
}

interface MetricCardProps {
  label: string
  value: string
  unit?: string
  /** Icon element (Lucide component, FA <i>, or any ReactNode) */
  icon: React.ReactNode
  /** Tailwind class for icon circle background (e.g. 'tw:bg-emerald-500/10') */
  iconBg?: string
  /** Tailwind color token for the border accent (e.g. 'emerald-500'). Must exist in BORDER_COLOR_CLASS. Omit for no border. */
  borderColor?: keyof typeof BORDER_COLOR_CLASS
  /** CSS color string for the value (e.g. '#10b981') */
  valueColor?: string
  /** Small text below the value */
  subtext?: string
  /** Custom rich subtext node (icon + label + value chip). Takes precedence over `subtext`. */
  subtextNode?: React.ReactNode
  /** YoY change percentage — auto-renders trending icon. Negative = green, positive = red. */
  change?: number
  isLoading?: boolean
  className?: string
}

const MetricCard = ({
  label,
  value,
  unit,
  icon,
  iconBg,
  borderColor,
  valueColor,
  subtext,
  subtextNode,
  change,
  isLoading,
  className,
}: MetricCardProps) => {
  const isReduction = change !== undefined && change < 0
  const changeColor = isReduction ? 'tw:text-emerald-600' : 'tw:text-red-600'
  const borderClass = borderColor ? cn('tw:border-l-[3px]', BORDER_COLOR_CLASS[borderColor]) : ''

  return (
    <Card className={cn('tw:h-full tw:py-4', borderClass, className)}>
      <CardContent className='tw:pb-0'>
        <div className='tw:flex tw:justify-between tw:items-start'>
          <div className='tw:flex-1'>
            <p className='tw:text-sm tw:text-muted-foreground tw:mb-2'>{label}</p>
            {isLoading ? (
              <Loader2 className='tw:size-5 tw:animate-spin tw:text-muted-foreground' />
            ) : (
              <>
                <div className='tw:flex tw:items-baseline tw:gap-1'>
                  <span
                    className='tw:text-2xl tw:font-bold tw:tracking-tight'
                    style={valueColor ? { color: valueColor } : undefined}
                  >
                    {value}
                  </span>
                  {unit && <span className='tw:text-sm tw:text-muted-foreground'>{unit}</span>}
                </div>
                {change !== undefined && (
                  <div className={cn('tw:flex tw:items-center tw:gap-1 tw:mt-1 tw:text-sm', changeColor)}>
                    {isReduction ? (
                      <TrendingDown className='tw:size-3.5' />
                    ) : (
                      <TrendingUp className='tw:size-3.5' />
                    )}
                    <span className='tw:font-medium'>{Math.abs(change).toFixed(1)}%</span>
                    <span className='tw:text-muted-foreground'>vs last year</span>
                  </div>
                )}
                {subtextNode && (
                  <div className='tw:mt-1'>{subtextNode}</div>
                )}
                {!subtextNode && subtext && (
                  <p className='tw:text-sm tw:text-muted-foreground tw:mb-0 tw:mt-1'>{subtext}</p>
                )}
              </>
            )}
          </div>
          <div className={cn('tw:rounded-full tw:flex tw:items-center tw:justify-center tw:size-11 tw:shrink-0', iconBg)}>
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// ============================================================================
// FeaturedMetricCard — hero gradient card (e.g. Total Emissions)
// ============================================================================

interface FeaturedMetricCardProps {
  label: string
  value: string
  unit?: string
  /** Icon element */
  icon: React.ReactNode
  /** YoY change percentage */
  change?: number
  isLoading?: boolean
  /** Override the default emerald gradient (provide full tw:bg-gradient-to-br class string) */
  className?: string
}

const FeaturedMetricCard = ({
  label,
  value,
  unit,
  icon,
  change,
  isLoading,
  className,
}: FeaturedMetricCardProps) => {
  const isReduction = change !== undefined && change < 0

  return (
    <Card className={cn('tw:h-full tw:py-4 tw:bg-gradient-to-br tw:from-emerald-700 tw:to-teal-800 tw:text-white tw:border-0', className)}>
      <CardContent className='tw:pb-0'>
        <div className='tw:flex tw:justify-between tw:items-start'>
          <div className='tw:flex-1'>
            <p className='tw:text-sm tw:text-emerald-100 tw:mb-2'>{label}</p>
            {isLoading ? (
              <Loader2 className='tw:size-5 tw:animate-spin tw:text-emerald-200' />
            ) : (
              <>
                <div className='tw:flex tw:items-baseline tw:gap-1'>
                  <span className='tw:text-2xl tw:font-bold tw:tracking-tight'>{value}</span>
                  {unit && <span className='tw:text-sm tw:text-emerald-200'>{unit}</span>}
                </div>
                {change !== undefined && (
                  <div className='tw:flex tw:items-center tw:gap-1 tw:mt-1 tw:text-sm tw:text-emerald-100'>
                    {isReduction ? (
                      <TrendingDown className='tw:size-3.5' />
                    ) : (
                      <TrendingUp className='tw:size-3.5' />
                    )}
                    <span className='tw:font-medium'>{Math.abs(change).toFixed(1)}%</span>
                    <span className='tw:text-emerald-200'>vs last year</span>
                  </div>
                )}
              </>
            )}
          </div>
          <div className='tw:rounded-full tw:flex tw:items-center tw:justify-center tw:size-11 tw:shrink-0 tw:bg-white/15'>
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export { MetricCard, FeaturedMetricCard }
export type { MetricCardProps, FeaturedMetricCardProps }
